import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import InvoiceActionModal from './InvoiceActionModal';
import DateRangeFilter, { getDateRange } from '../../../components/DateRangeFilter';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d?.getTime())) return '—';
  return d?.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
};

const InvoiceCenterTab = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [preset, setPreset] = useState('last_30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [modalState, setModalState] = useState({ isOpen: false, invoice: null, actionType: null });
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const extractState = (address) => {
    if (!address) return '';
    const parts = address.split(',').map(p => p.trim());
    return parts[parts.length - 1]?.toLowerCase() || '';
  };

  const buildInvoices = useCallback(async (from, to) => {
    setLoading(true);
    try {
      if (!user?.email && !user?.id) { setInvoices([]); return; }

      const { data: { session } } = await supabase?.auth?.getSession();
      const userEmail = session?.user?.email || user?.email;
      const userId = session?.user?.id || user?.id;

      let query = supabase
        ?.from('buyer_bookings')
        ?.select('id, readable_booking_id, factory_name, grade, quantity, rate_per_ton, total_amount, status, created_at, booking_date, dispatched_at, buyer_email, buyer_id, loading_from, destination, assigned_vehicle_number, booking_type')
        ?.in('status', ['in_transit', 'completed', 'delivered'])
        ?.order('created_at', { ascending: false });

      if (from) query = query.gte('booking_date', from.toISOString().slice(0, 10));
      if (to) query = query.lte('booking_date', to.toISOString().slice(0, 10));

      const { data, error } = await query;
      if (error) throw error;

      const filtered = (data || [])?.filter(b =>
        b?.buyer_email === userEmail || b?.buyer_id === userId
      );

      const derived = filtered?.map((b, idx) => {
        const subtotal = Number(b?.total_amount) || (Number(b?.quantity) * Number(b?.rate_per_ton)) || 0;
        const sourceState = extractState(b?.loading_from);
        const destState = extractState(b?.destination);
        const isInterState = sourceState && destState && sourceState !== destState;
        const igst = isInterState ? subtotal * 0.05 : 0;
        const cgst = isInterState ? 0 : subtotal * 0.025;
        const sgst = isInterState ? 0 : subtotal * 0.025;
        const totalAmount = subtotal + igst + cgst + sgst;
        const bookingDate = b?.booking_date || b?.created_at || new Date()?.toISOString();

        return {
          id: `INV${String(idx + 1)?.padStart(7, '0')}`,
          bookingId: b?.readable_booking_id || b?.id,
          invoiceNumber: `SH/INV/${new Date(bookingDate)?.getFullYear()}/${String(idx + 1)?.padStart(3, '0')}`,
          date: bookingDate?.slice(0, 10),
          mill: b?.factory_name || '—',
          product: b?.grade || '—',
          quantity: Number(b?.quantity) || 0,
          rate: Number(b?.rate_per_ton) || 0,
          vehicleNumber: b?.assigned_vehicle_number || '—',
          destination: b?.destination || '—',
          loadingFrom: b?.loading_from || '—',
          subtotal,
          igst,
          cgst,
          sgst,
          isInterState,
          totalAmount,
          status: (() => {
            if (b?.status === 'completed' || b?.status === 'delivered') return 'Delivered';
            if (b?.status === 'in_transit' && b?.dispatched_at) {
              const days = (Date.now() - new Date(b.dispatched_at).getTime()) / (1000 * 60 * 60 * 24);
              return days >= 4 ? 'Delivered' : 'Pending';
            }
            return 'Pending';
          })(),
          paymentDate: (b?.status === 'completed' || b?.status === 'delivered') ? bookingDate?.slice(0, 10) : null,
          gstNumber: '—',
        };
      });

      setInvoices(derived);
    } catch (err) {
      console.error('InvoiceCenterTab error:', err?.message);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const { from: dateFrom, to: dateTo } = getDateRange(preset, customFrom, customTo);

  useEffect(() => {
    buildInvoices(dateFrom, dateTo);
  }, [buildInvoices, preset, customFrom, customTo]);

  const getStatusColor = (status) => {
    const colors = {
      'Paid': 'bg-green-100 text-green-800',
      'Delivered': 'bg-green-100 text-green-800',
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Overdue': 'bg-red-100 text-red-800',
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const handleInvoiceAction = (invoice, actionType) => {
    setModalState({ isOpen: true, invoice, actionType });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, invoice: null, actionType: null });
  };

  const filteredInvoices = invoices?.filter(inv => {
    const q = searchQuery?.toLowerCase() || '';
    return !q || [
      inv?.invoiceNumber, inv?.bookingId, inv?.mill,
      inv?.product, inv?.vehicleNumber, inv?.destination
    ].some(f => (f || '')?.toLowerCase()?.includes(q));
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Invoice Center</h2>
          <p className="text-sm text-muted-foreground mt-1">GST-compliant invoices and documentation</p>
        </div>
        <Input
          type="search"
          placeholder="Search invoices..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          className="w-full sm:w-64"
        />
      </div>

      <DateRangeFilter
        preset={preset} customFrom={customFrom} customTo={customTo}
        onPresetChange={setPreset} onCustomFromChange={setCustomFrom} onCustomToChange={setCustomTo}
      />

      {/* Loading */}
      {loading && (
        <div className="text-center py-12 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
          <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm" style={{ color: '#9ca3af' }}>Loading invoices...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && invoices?.length === 0 && (
        <div className="text-center py-16 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
          <Icon name="FileText" size={40} color="#d1a87a" />
          <p className="mt-3 text-sm font-medium" style={{ color: '#9ca3af' }}>No invoices found</p>
          <p className="text-xs mt-1" style={{ color: '#d1a87a' }}>
            Try a different date range to find older records
          </p>
        </div>
      )}

      {/* No results after filter */}
      {!loading && invoices?.length > 0 && filteredInvoices?.length === 0 && (
        <div className="text-center py-12 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
          <Icon name="Search" size={32} color="#d1a87a" />
          <p className="mt-3 text-sm font-medium" style={{ color: '#9ca3af' }}>No invoices match your search</p>
        </div>
      )}

      <div className="grid gap-6">
        {filteredInvoices?.map((invoice) => (
          <div key={invoice?.id} className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200">
            <div className="bg-muted px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <Icon name="FileText" size={20} className="text-primary" />
                <div>
                  <span className="text-sm font-semibold text-foreground">{invoice?.invoiceNumber}</span>
                  <p className="text-xs text-muted-foreground">Invoice ID: {invoice?.id}</p>
                </div>
              </div>
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(invoice?.status)} w-fit`}>
                {invoice?.status}
              </span>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Mill Name</p>
                    <p className="text-sm font-medium text-foreground">{invoice?.mill}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">GST Number</p>
                    <p className="text-sm font-medium text-foreground font-data">{invoice?.gstNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Invoice Date</p>
                    <p className="text-sm font-medium text-foreground">{formatDate(invoice?.date)}</p>
                  </div>
                  {invoice?.paymentDate && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Payment Date</p>
                      <p className="text-sm font-medium text-foreground">{formatDate(invoice?.paymentDate)}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Product</p>
                    <p className="text-sm font-medium text-foreground">{invoice?.product}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Quantity</p>
                      <p className="text-sm font-medium text-foreground">{invoice?.quantity} MT</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Rate</p>
                      <p className="text-sm font-medium text-foreground">₹{invoice?.rate?.toLocaleString('en-IN')}/MT</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Booking Reference</p>
                    <p className="text-sm font-medium text-foreground">{invoice?.bookingId}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-foreground font-medium">₹{invoice?.subtotal?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
                {invoice?.isInterState ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">IGST (5%)</span>
                    <span className="text-foreground">₹{invoice?.igst?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">CGST (2.5%)</span>
                      <span className="text-foreground">₹{invoice?.cgst?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">SGST (2.5%)</span>
                      <span className="text-foreground">₹{invoice?.sgst?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-base font-semibold pt-2 border-t border-border">
                  <span className="text-foreground">Total Amount</span>
                  <span className="text-primary">₹{invoice?.totalAmount?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button variant="default" size="sm" iconName="Download" iconPosition="left"
                  onClick={() => handleInvoiceAction(invoice, 'download')} className="w-fit">
                  Download PDF
                </Button>
                <Button variant="outline" size="sm" iconName="FileText" iconPosition="left"
                  onClick={() => handleInvoiceAction(invoice, 'einvoice')} className="w-fit">
                  View eInvoice
                </Button>
                <Button variant="secondary" size="sm" iconName="Truck" iconPosition="left"
                  onClick={() => handleInvoiceAction(invoice, 'eway')} className="w-fit">
                  eWay Bill
                </Button>
                <Button variant="ghost" size="sm" iconName="Printer" iconPosition="left"
                  onClick={() => handleInvoiceAction(invoice, 'print')} className="w-fit">
                  Print
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <InvoiceActionModal
        isOpen={modalState?.isOpen}
        invoice={modalState?.invoice}
        actionType={modalState?.actionType}
        onClose={closeModal}
      />
    </div>
  );
};

export default InvoiceCenterTab;

